import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { authApi } from '@/entities/auth/api/auth.api';
import { getRelationshipGroup } from '@/entities/business-relationship/lib/statusView';
import { PrescriptionUploadDialog } from '@/entities/prescription/components/PrescriptionUploadDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { showNetworkErrorToast } from '@/shared/utils/toast';
import { useUIStore } from '@/stores/uiStore';

type HostMode =
  'idle' | 'checking' | 'upload' | 'pending' | 'closed' | 'awaitingApproval';

type BlockedMode = 'pending' | 'closed' | 'awaitingApproval';

/** M-07 §4 등록 불가 팝업 문구 (Figma Pop-Up 905:13389) */
const BLOCKED_DIALOG: Record<BlockedMode, { title: string; message: string }> =
  {
    pending: {
      title: '등록할 수 없어요',
      message: '거래 승인 후 처방 내역을 등록할 수 있어요.',
    },
    closed: {
      title: '등록할 수 없어요',
      message: '거래가 종료된 거래처는 처방을 등록할 수 없어요.',
    },
    // 앱 handlePrescriptionUpload(prescription_upload_helper.dart)와 같은 문구
    awaitingApproval: {
      title: '조금만 기다려 주세요',
      message: '관리자의 승인 후\n처방 내역 등록이 가능합니다.',
    },
  };

/**
 * M-06 처방 내역 등록 모달 호스트 — AppLayout에 한 번만 둔다.
 * 여는 쪽은 useUIStore().openPrescriptionUpload({ relationshipId, clinicName, statusView })를 호출한다.
 *
 * M-07 분기표를 먼저 적용한다 (서버에 이 가드가 없어 웹이 막는다, docs/api C1):
 * - 거래 중 · 거래처 승인 + salesApprovalStatus === SETTLEMENT_POSSIBLE → 등록 모달
 * - 거래 신청 · 확인 중 · 이관 확인 중 → "거래 승인 후…" 팝업
 * - 거래 종료 · 신청 거절 → "거래가 종료된…" 팝업
 * - salesApprovalStatus 분기는 앱과 같다:
 *   - TRANSACTION_IMPOSSIBLE(필수 정보 미등록) → X6 공통 앱 안내 (앱은 필수정보 등록 화면으로 보낸다)
 *   - 그 밖(TRANSACTION_POSSIBLE 승인 대기 등) → "조금만 기다려 주세요" 팝업
 */
export const PrescriptionUploadHost = () => {
  const target = useUIStore((s) => s.prescriptionUpload);
  const close = useUIStore((s) => s.closePrescriptionUpload);
  const openAppOnly = useUIStore((s) => s.openAppOnly);
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<HostMode>('idle');

  useEffect(() => {
    if (!target) {
      setMode('idle');
      return;
    }
    const group = getRelationshipGroup(target.statusView);
    if (group !== 'ongoing') {
      setMode(group === 'closed' ? 'closed' : 'pending');
      return;
    }

    // 사업자 승인 상태는 앱에서 바뀔 수 있어 열 때마다(1분 캐시) 다시 확인한다
    let cancelled = false;
    setMode('checking');
    queryClient
      .fetchQuery({
        queryKey: ['auth', 'me'],
        queryFn: authApi.me,
        staleTime: 60 * 1000,
      })
      .then((me) => {
        if (cancelled) return;
        if (me.salesApprovalStatus === 'SETTLEMENT_POSSIBLE') {
          setMode('upload');
        } else if (me.salesApprovalStatus === 'TRANSACTION_IMPOSSIBLE') {
          close();
          openAppOnly('businessInfo');
        } else {
          setMode('awaitingApproval');
        }
      })
      .catch(() => {
        if (cancelled) return;
        close();
        showNetworkErrorToast();
      });
    return () => {
      cancelled = true;
    };
  }, [target, queryClient, close, openAppOnly]);

  if (!target) return null;

  if (mode === 'pending' || mode === 'closed' || mode === 'awaitingApproval') {
    const { title, message } = BLOCKED_DIALOG[mode];
    return (
      <AlertDialog open onOpenChange={(open) => !open && close()}>
        <AlertDialogContent>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription className="whitespace-pre-line">
            {message}
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogAction>확인</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  if (mode === 'upload') {
    return <PrescriptionUploadDialog target={target} onClose={close} />;
  }

  return null;
};
